import { useState } from "react"
import { useRouter } from "next/router"
import { AuthorCard } from "../components/blogs"
import { FooterSection, HeaderSection } from "../components/layout"
import { Loader } from "../components/common"
import styles from "../styles/pages/AuthorsPage.module.css"
import { fetchAllAuthors, fetchConfigPage } from "../utils/api"
import SEOHead, { getPageSEO } from "../utils/helpers/seo"
import { useDarkMode } from "../utils/hooks/useDarkMode"

export default function AuthorsPage({ initialData, error }) {
  const router = useRouter()
  const [authors] = useState(initialData?.authorsData?.data || [])

  // Dark mode hook
  const { isDarkMode, isThemeInitialized } = useDarkMode()

  // Priority 1: Show error if there's an error
  if (error) {
    return <Loader type="error" message="Oops! Something Went Wrong" description="Please wait a moment and try again. We'll be back up shortly!" fullPage={true} action={() => window.location.reload()} actionText="Refresh Page" />;
  }

  // Priority 2: Show loading if no data yet
  if (!initialData) {
    return <Loader type="loading" message="Loading authors..." description="Please wait while we fetch the authors." fullPage={true} />;
  }

  // Extract content from data
  const configData = initialData?.configData || {}
  const header = configData?.header || {}
  const footer = configData?.footer || {}

  // Generate SEO data for this authors page
  const seoData = getPageSEO(
    {
      title: "Our Authors",
      Description: "Meet the talented writers behind our blog. Discover their expertise and read their latest articles.",
    },
    configData,
    '/authors'
  );

  return (
    <>
 <SEOHead
        pageData={{
          title: "Our Authors",
          Description: "Written by the HireGo content team, our blog delivers trusted travel guidance with reliable tips for hiring a minibus in the UK.",
        }}
        configData={configData}
        pageUrl="/authors"
        customSEO={{
          title: "Our Authors | HireGo Blog",
          description: "Here is our talented team of writers behind the HireGo blog. Read on to learn about practical insights, tips, travel guidance and the latest update for travel across the United Kingdom.",
          canonical: `${configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hireaminibuswithdriver.co.uk/'}authors`,
          openGraph: {
            title: "Our Authors | HireGo Blog",
            description: "Our expert writers behind the HireGo blog share travel insights, guidance, tips and tricks for an easy travel around the UK.",
            type: 'website',
            url: `${configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hireaminibuswithdriver.co.uk/'}authors`,
          },
          twitter: {
            title: "Our Authors | HireGo Blog",
            description: "HireGo authors are the industry-experts on the subject matter and provide all the guidance and news updates for travel around the United Kingdom.",
          },
        }}
      />

      <HeaderSection data={header} pageUrl="/authors" />

      <main className={styles.mainContent}>
        {/* Enhanced Hero Section */}
        <section className={styles.heroSection}>
          {/* <div className={styles.heroBackground}> */}
            {/* <div className={styles.heroPattern}></div> */}
            {/* <div className={styles.heroGradient}></div> */}
          {/* </div> */}
          <div className="tw-container">
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>Meet Our Team</span>
              </div>
              <h1 className={styles.heroTitle}>
                <span className={styles.titleHighlight}>Our Authors</span>
              </h1>
              <p className={styles.heroDescription}>
              Our expert writers behind the HireGo blog share travel insights, guidance, tips and tricks for an easy travel around the UK.
              </p>
              {/* <div className={styles.heroDivider}></div> */}
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        {authors.length > 0 && (
          <section className={styles.statsSection}>
            <div className="tw-container">
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <div className={styles.statValue}>{authors.length}</div>
                  <div className={styles.statLabel}>Expert Authors</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                  <div className={styles.statValue}>
                    {authors.reduce((total, author) => total + (author?.blogs?.length || 0), 0)}
                  </div>
                  <div className={styles.statLabel}>Total Articles</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                  </div>
                  <div className={styles.statValue}>
                    {new Set(authors.flatMap(author => author?.expertises || [])).size}
                  </div>
                  <div className={styles.statLabel}>Expertise Areas</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Authors Content */}
        <section className={styles.authorsSection}>
          <div className="tw-container">
            {authors.length === 0 ? (
              <div className={styles.noAuthorsMessage}>
                <div className={styles.noAuthorsIcon}>
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3>No Authors Found</h3>
                <p>
                  We couldn&apos;t find any authors at the moment. Please check back later.
                </p>
              </div>
            ) : (
              <div className={styles.authorsGrid}>
                {authors.map((author, index) => (
                  <div key={author?.id || index} className={styles.authorCardWrapper} style={{ animationDelay: `${index * 0.08}s` }}>
                    <AuthorCard author={author} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <FooterSection data={footer} />
    </>
  )
}

// Server-side data fetching
export async function getServerSideProps(context) {
  try {
    // Fetch all required data in parallel
    const [configData, authorsResponse] = await Promise.all([
      fetchConfigPage(),
      fetchAllAuthors({ 
        page: 1, 
        pageSize: 100 // Fetch all authors
      })
    ])

    // Check if essential data is missing (return empty structure, not null)
    if (!configData || !authorsResponse) {
      return {
        props: {
          initialData: {
            configData: configData || {},
            authorsData: { data: [], meta: { pagination: { page: 1, pageCount: 1, total: 0 } } },
          },
          error: null,
        },
      }
    }

    // Return valid data
    return {
      props: {
        initialData: {
          configData: configData,
          authorsData: authorsResponse,
        },
        error: null,
      },
    }
  } catch (error) {
    console.error('Error fetching authors page data:', error)
    // Return error (with null initialData)
    return {
      props: {
        initialData: null,
        error: error.message || 'An unexpected error occurred while loading authors.',
      },
    }
  }
}

